import React, { useState, useEffect } from "react";
import cancel from "../assets/images/navbar/cancel.png";
import Cuisines from "../components/Cuisines";
import RestaurantsByCuisineModal from "./RestaurantsByCuisineModal";
import { categoryService } from "../services/index";
import { Category } from "../utils/types";

interface AllCuisinesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AllCuisinesModal: React.FC<AllCuisinesModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [cuisines, setCuisines] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCuisine, setSelectedCuisine] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isRestaurantModalOpen, setIsRestaurantModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen) return;

    const fetchAllCuisines = async () => {
      try {
        setLoading(true);
        const response: any = await categoryService.getCategories({
          page: 1,
          limit: 1000, // Large limit to get all cuisines
          sortBy: 'sortOrder',
          sortOrder: 'asc',
          isActive: true
        });
        
        // Handle response based on API structure
        const data = response.data || response;
        setCuisines(Array.isArray(data) ? data : []);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching all cuisines:', err);
        setError(err.message || 'Failed to load cuisines');
      } finally {
        setLoading(false);
      }
    };

    fetchAllCuisines();
  }, [isOpen]);

  const handleCuisineClick = (cuisineId: string, cuisineName: string) => {
    setSelectedCuisine({ id: cuisineId, name: cuisineName });
    setIsRestaurantModalOpen(true);
  };

  const handleCloseRestaurantModal = () => {
    setIsRestaurantModalOpen(false);
    setSelectedCuisine(null);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop Overlay - hidden on mobile (full screen), visible on desktop */}
      <div
        className="hidden md:block fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-[1003] transition-colors duration-300"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="fixed inset-0 md:flex md:items-center md:justify-center z-[1004] md:pointer-events-none">
        <div
          className="h-full w-full md:h-auto md:w-[90vw] md:max-w-[900px] md:max-h-[85vh] bg-white dark:bg-gray-800 md:rounded-[24px] md:pointer-events-auto relative transition-colors duration-300 overflow-hidden flex flex-col"
          style={{
            boxShadow: "-23px 23px 72px 0 rgba(0, 0, 0, 0.15)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with Back/Close Button */}
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <h2 className="text-gray-900 dark:text-white font-urbanist text-xl md:text-2xl font-semibold">
              All Cuisines
            </h2>
            
            {/* Close Button */}
            <div
              className="h-[36px] w-[36px] cursor-pointer border dark:border-gray-600 rounded-full p-[7px] bg-white dark:bg-gray-700 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              onClick={onClose}
            >
              <img src={cancel} alt="Close" className="h-[18px] w-[18px]" />
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <p className="text-gray-600 dark:text-gray-400">Loading cuisines...</p>
              </div>
            ) : error ? (
              <div className="flex justify-center items-center py-8">
                <p className="text-red-600 dark:text-red-400">Error: {error}</p>
              </div>
            ) : cuisines.length === 0 ? (
              <div className="flex justify-center items-center py-8">
                <p className="text-gray-600 dark:text-gray-400">No cuisines found</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3 md:gap-4">
                {cuisines.map((item) => (
                  <Cuisines
                    key={item._id}
                    index={item.sortOrder}
                    name={item.name}
                    image={item.image}
                    _id={item._id}
                    onClick={handleCuisineClick}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Restaurants by Cuisine Modal */}
      {selectedCuisine && (
        <RestaurantsByCuisineModal
          isOpen={isRestaurantModalOpen}
          onClose={handleCloseRestaurantModal}
          cuisineId={selectedCuisine.id}
          cuisineName={selectedCuisine.name}
        />
      )}
    </>
  );
};

export default AllCuisinesModal;
